import {
	addKeyToFieldArrayItem,
	arrayMutators,
	EmptyNumberValidator,
	Form,
	GetFieldValue,
	isTruthy,
	SelectField,
	SubmissionError,
	SwitchInputField,
	TextAreaInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useXHR } from "@sembark-travel/xhr"
import { useId, useMemo, useState } from "react"
import useSWR from "swr"
import {
	getValidTravelActivityData,
	TravelActivityDatewisePricesInputField,
	TTravelActivityDatewisePricesInputFieldValue,
	TValidTravelActivityDatewisePricesInputFieldValue,
} from "../TravelActivityPrices"
import { ITrip } from "../Trips"
import { TTravelActiviytBooking } from "./store"

import {
	dateToUTCString,
	formatDate,
	getDiff,
	isSame,
	localOrUtcTimestampToLocalDate,
	parseDate,
} from "@sembark-travel/datetime-utils"
import { isSameMoney, moneyParseByDecimal, TMoney } from "@sembark-travel/money"
import { withOrdinalSuffix } from "@sembark-travel/number-utils"
import {
	Alert,
	Box,
	Button,
	Card,
	CardBody,
	Col,
	Component,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Money,
	Select,
	Spinner,
	Stack,
	Table,
	TableDataCell,
	TableHeaderDataCell,
	Text,
	Time,
} from "@sembark-travel/ui-base"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	useDialog,
} from "@sembark-travel/ui-dialog"
import { Optional } from "utility-types"
import * as Validator from "yup"
import {
	TChildrenArray,
	TTourist,
	TTouristGroup,
	TouristAssignmentInputField,
	ITouristOrGroupOption,
	TBookingTouristUpdate,
} from "../Tourists"
import { createBookingDateSelectionOptionsFromStartAndEndDates } from "../TransportServicePrices/utils"
import {
	ISupplier,
	SelectSupplierContact,
	SelectTravelActivitySuppliers,
	TSupplierContact,
	ActivityPaymentPreferencesForm,
} from "../Supplier"
import {
	getClosedDates,
	showClosedServicesAlert,
	TTravelActivity,
	TTravelActivityTicketType,
	TTravelActivityTicketTouristConfiguration,
} from "../TravelActivities"
import { getActivityDurationSlotAndOptions } from "../TravelActivityPrices/utils"
import { TTripDestination } from "../TripDestinations"
import { getStartEndDateTimesFromDateAndSlot } from "./utils"
import { TTransportServiceLocationPoint } from "../TransportServices"
import { extractLocationString } from "../TransportServices/utils"

// Type for ticket tourist configurations with proper typing
type TTicketTouristConfiguration = {
	__id: number
	quantity?: number
	complementary?: number
	configuration?: TTravelActivityTicketTouristConfiguration
	fetching_prices?: boolean
	date_wise_prices?: Array<{
		__id: number
		date: Date
		currency: string
		price?: number
		given_price?: number
		per_quantity_price?: number
		per_quantity_given_price?: number
		per_quantity_booked_price?: number
		booked_price?: number
		edited_booked_price?: boolean
	}>
}

function useBookingDetails(bookingId: number | string) {
	const xhr = useXHR()
	const id = useId()
	const { data } = useSWR(`/travel-activity-bookings/${bookingId}?${id}`, () =>
		xhr
			.get<{
				data: TTravelActiviytBooking & {
					trip: ITrip
				}
			}>(`/travel-activity-bookings/${bookingId}`)
			.then((resp) => resp.data.data)
	)
	return { data }
}

export function EditTravelActivityBookingItemInDialog({
	children,
	...props
}: Optional<
	Omit<React.ComponentProps<typeof EditTravelActivityBookingItem>, "onCancel">,
	"onSuccess"
> & {
	children: (props: { onEdit: () => void }) => React.ReactNode
}) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	return (
		<>
			{children({ onEdit: openDialog })}
			<Dialog
				open={isOpen}
				onClose={closeDialog}
				title="Edit Travel Activity Booking"
				xl
			>
				<Dialog.Body>
					<EditTravelActivityBookingItem
						{...props}
						onSuccess={() => {
							closeDialog()
							props.onSuccess?.()
						}}
						onCancel={closeDialog}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export function EditTravelActivityBookingItem({
	bookingId,
	onSuccess,
	...props
}: {
	bookingId: number | string
	onSuccess: () => unknown
} & Pick<React.ComponentProps<typeof EditForm>, "onCancel">) {
	const xhr = useXHR()
	const { data } = useBookingDetails(bookingId)

	// State to store the change details
	const [changeDetails, setChangeDetails] = useState<{
		initialPrice: number
		currentPrice: number
		initialConfigurations: Array<{
			configuration_name: string
			quantity: number
			complementary: number
			per_quantity_price: TMoney
			total_price: TMoney
			currency: string
		}>
		currentConfigurations: Array<{
			configuration_name: string
			quantity: number
			complementary: number
			per_quantity_price: TMoney
			total_price: TMoney
			currency: string
		}>
		currency: string
	} | null>(null)

	const dates = useMemo(() => {
		if (!data?.start_date) return []
		return [
			localOrUtcTimestampToLocalDate(data.start_date_local, data.start_date),
		]
	}, [data?.start_date, data?.start_date_local])
	if (!data) {
		return <Spinner padding="4" alignCenter />
	}
	const {
		activity,
		trip,
		ticket_type,
		duration,
		slot,
		ticket_tourist_configurations: initialTicketTouristConfigurations,
		comments,
		supplier,
		supplier_contact,
		confirmation_details,
		remarks,
		is_booked,
		tourists,
		tourist,
		members,
		booked_price: initialBookedPrice,
		currency,
	} = data
	const { no_of_adults, children, destinations, id: tripId } = trip
	const { durationOption, activitySlot, activitySlotsOptions } =
		getActivityDurationSlotAndOptions(ticket_type, duration, slot)

	// Store initial values for comparison
	const initialActivityId = activity.id
	const initialTicketTypeId = ticket_type?.id

	return (
		<ConfirmationDialogProvider>
			{({ confirm }) => (
				<>
					<EditForm
						tripId={tripId}
						noOfAdults={no_of_adults}
						children={children}
						tripDestinations={destinations}
						dates={dates}
						tripData={trip}
						bookingData={data}
						initialValues={{
							supplier,
							supplier_contact,
							confirmation_details,
							remarks,
							booked: isTruthy(is_booked),
							data: {
								dates,
								activity,
								ticket_type,
								duration: durationOption,
								slot: activitySlot,
								activity_slots: activitySlotsOptions,
								comments,
								has_complementary: initialTicketTouristConfigurations.some(
									(t) => t.complementary > 0
								),
								pick_up_location: data.pick_up_location,
								drop_location: data.drop_location,
								use_hotel_as_pickup: data.use_hotel_as_pickup,
								use_hotel_as_drop: data.use_hotel_as_drop,
								ticket_tourist_configurations:
									initialTicketTouristConfigurations.map(
										({
											complementary,
											quantity,
											configuration,
											booked_price,
											currency,
											per_quantity_booked_price,
										}) =>
											addKeyToFieldArrayItem({
												complementary,
												quantity,
												configuration,
												date_wise_prices: [
													addKeyToFieldArrayItem({
														date: dates[0],
														currency,
														per_quantity_booked_price,
														booked_price,
														edited_booked_price: true,
													}),
												],
											})
									),
							},
							all_tourists: tourists || [],
							primary_contact: tourist ? tourist.id : undefined,
							members: (members || []).map((m) => {
								const { member, member_id, member_type } = m
								return {
									id:
										member_type === "tourists"
											? `tourist-${member.id}`
											: `group-${member.id}`,
									member_type: member_type,
									member_id: member_id,
									name: member.name,
									member: member,
								}
							}),
							tourists: tourists?.map((t) => ({
								id: t.id,
								remarks: t.booking_tourist?.remarks ?? null,
							})),
						}}
						onSubmit={async ({
							supplier,
							supplier_contact,
							confirmation_details,
							remarks,
							booked,
							data,
							selectedDates,
							tourists,
							primary_contact,
							members,
						}) => {
							if (data.ticket_type && selectedDates) {
								const closedActivity = getClosedDates(
									data.ticket_type,
									selectedDates,
									data.ticket_type.name
								)
								if (Object.keys(closedActivity).length) {
									const alertHeader = "Some of the activities are closed. \n"
									const alertMessage = showClosedServicesAlert(closedActivity)
									const alertFooter =
										"\n Please update the activities before bookings."
									alert(alertHeader + alertMessage + alertFooter)
									return
								}
							}
							const {
								activity,
								ticket_type,
								slot,
								ticket_tourist_configurations,
								comments,
								pick_up_location,
								drop_location,
								use_hotel_as_drop,
								use_hotel_as_pickup,
							} = data
							const { start_date, end_date, start_time, end_time, duration } =
								getStartEndDateTimesFromDateAndSlot(
									selectedDates?.[0] || dates[0],
									slot,
									data.duration?.iso ? data.duration.iso : undefined
								)

							const memberTouristId: number[] = members
								? Array.from(
										new Set(
											members.flatMap((m) => {
												if (m.member_type === "tourists") {
													return [m.member_id]
												} else if (m.member_type === "tourist_groups") {
													const group = m.member as TTouristGroup
													return group.members.map((gm) => gm.tourist.id)
												}
												return []
											})
										)
									)
								: []
							const payload = {
								supplier: supplier?.id,
								supplier_contact: supplier ? supplier_contact?.id : undefined,
								confirmation_details,
								remarks,
								booked: isTruthy(booked) ? 1 : 0,
								start_date: dateToUTCString(start_date),
								end_date: dateToUTCString(end_date),
								start_date_local: formatDate(start_date, "YYYY-MM-DD"),
								start_time_local: start_time,
								end_date_local: formatDate(end_date, "YYYY-MM-DD"),
								end_time_local: end_time,
								duration: duration,
								activity_id: activity.id,
								ticket_type_id: ticket_type?.id,
								comments,
								pick_up_location: use_hotel_as_pickup
									? undefined
									: pick_up_location,
								drop_location: use_hotel_as_drop ? undefined : drop_location,
								use_hotel_as_pickup,
								use_hotel_as_drop,
								ticket_tourist_configurations:
									ticket_tourist_configurations.map(
										({
											configuration,
											quantity,
											date_wise_prices,
											complementary,
										}) => {
											const {
												price,
												per_quantity_given_price,
												given_price,
												per_quantity_booked_price,
												booked_price,
												currency,
											} = date_wise_prices?.at(0) || {
												currency: "INR",
												price: 0,
												given_price: 0,
												per_quantity_given_price: 0,
												booked_price: 0,
												per_quantity_booked_price: 0,
											}
											return {
												configuration_id: configuration.id,
												quantity,
												complementary,
												currency,
												calculated_price: price,
												per_quantity_given_price: per_quantity_given_price || 0,
												given_price: given_price || 0,
												booked_price: booked_price || 0,
												per_quantity_booked_price:
													per_quantity_booked_price || 0,
											}
										}
									),
								tourists: tourists
									? tourists
											?.map((t) => ({
												id: t.id,
												remarks:
													t.remarks && t.remarks.trim()
														? t.remarks.trim()
														: null,
											}))
											.filter((t) =>
												memberTouristId.find((mtId) => mtId === t.id)
											)
									: undefined,
								members: (members || []).length
									? (members || []).map((m) => ({
											type: m.member_type,
											id: m.member_id,
										}))
									: undefined,
								primary_tourist: (members || []).length
									? primary_contact
									: undefined,
							}
							// Check for changes in activity, ticket type, or tourist configurations
							const activityChanged = payload.activity_id !== initialActivityId
							const ticketTypeChanged =
								payload.ticket_type_id !== initialTicketTypeId

							// Calculate current total price for comparison
							const currentTotalPrice =
								payload.ticket_tourist_configurations.reduce(
									(total, config) => total + (Number(config.booked_price) || 0),
									0
								)

							// Check if price has changed
							const priceChanged = !isSameMoney(
								moneyParseByDecimal(currentTotalPrice, currency),
								moneyParseByDecimal(initialBookedPrice, currency)
							)

							// Show confirmation dialog if:
							// 1. Price has changed AND activity, and ticket type are unchanged
							// 2. OR if we have zero prices with initial prices (existing logic)
							const shouldShowConfirmation =
								priceChanged && !activityChanged && !ticketTypeChanged

							if (shouldShowConfirmation) {
								// Prepare initial configurations data
								// Get original prices directly from Initial data, not from form's date_wise_prices
								const initialConfigurations =
									initialTicketTouristConfigurations.map((config) => {
										const {
											configuration,
											quantity,
											complementary,
											per_quantity_booked_price,
											booked_price,
											currency,
										} = config

										// Calculate per-quantity price from total price and quantity
										// Use the stored per_quantity_booked_price if available, otherwise calculate it
										const qty = quantity || 0
										const totalPriceMoney = moneyParseByDecimal(
											booked_price || 0,
											currency
										)
										const calculatedPerQuantityPrice =
											qty > 0 ? (booked_price || 0) / (qty - complementary) : 0
										const perQuantityPriceMoney = moneyParseByDecimal(
											per_quantity_booked_price || calculatedPerQuantityPrice,
											currency
										)

										return {
											configuration_name: configuration.name,
											quantity: qty,
											complementary: complementary,
											per_quantity_price: perQuantityPriceMoney,
											total_price: totalPriceMoney,
											currency: currency,
										}
									})

								// Prepare current configurations data
								const currentConfigurations =
									payload.ticket_tourist_configurations.map(
										({
											configuration_id,
											quantity,
											per_quantity_booked_price,
											booked_price,
											complementary,
											currency,
										}) => {
											// Find the configuration name from initial data
											const initialConfig = ticket_tourist_configurations.find(
												(tc) => tc.configuration.id === configuration_id
											)
											const configCurrency = currency || "INR"
											return {
												configuration_name:
													initialConfig?.configuration.name || "Unknown",
												quantity: quantity || 0,
												complementary: complementary || 0,
												per_quantity_price: moneyParseByDecimal(
													per_quantity_booked_price || 0,
													configCurrency
												),
												total_price: moneyParseByDecimal(
													booked_price || 0,
													configCurrency
												),
												currency: configCurrency,
											}
										}
									)

								// Store change details for the dialog
								setChangeDetails({
									initialPrice: initialBookedPrice,
									currentPrice: currentTotalPrice,
									initialConfigurations,
									currentConfigurations,
									currency: currency || "N/A",
								})

								// Show confirmation dialog
								const confirmed = await confirm()
								if (!confirmed) {
									return
								}
							}
							await xhr.patch(`/travel-activity-bookings/${bookingId}`, payload)
							onSuccess()
						}}
						{...props}
					/>
					<ConfirmationDialog
						title="Confirm Price Change"
						acceptActionLabel="Yes,  Update And Save"
						rejectActionLabel="No, Go Back"
						destructiveAction={true}
					>
						<Stack gap="4">
							{changeDetails && (
								<>
									<Alert status="warning">
										The Booking rates have been modified. Please review the
										changes before confirming.
									</Alert>
									<Inline gap="4">
										<Stack
											borderWidth="1"
											gap="2"
											justifyContent="between"
											borderRadius="md"
											flex="1"
										>
											<Inline
												padding="2"
												gap="2"
												alignItems="center"
												fontSize="md"
												bgColor="warning"
												color="warning"
											>
												<Icons.RefreshClock size="6" />{" "}
												<Text>Previous Price</Text>
											</Inline>
											<Box paddingX="4" fontWeight="semibold">
												<Money
													showCurrency
													currency={changeDetails.currency}
													amount={changeDetails.initialPrice}
												/>
											</Box>

											{changeDetails.initialConfigurations.map(
												(config, index) => (
													<Box paddingX="2" paddingBottom="2" key={index}>
														<Inline
															bgColor="gray2"
															padding="2"
															borderRadius="md"
															gap="1"
														>
															<Money
																money={config.per_quantity_price}
																showCurrency
															/>
															<Text as="span">
																/{config.configuration_name} × {config.quantity}
																{config.complementary > 0 ? (
																	<Box title="Free of Cost">
																		({config.complementary} FOC)
																	</Box>
																) : (
																	""
																)}
															</Text>
														</Inline>
													</Box>
												)
											)}
										</Stack>
										<Stack borderWidth="1" flex="1" gap="2" borderRadius="md">
											<Inline
												padding="2"
												gap="2"
												alignItems="center"
												fontSize="md"
												bgColor="success"
											>
												<Icons.OkCircle size="6" /> <Text>Updated Price</Text>
											</Inline>
											<Box paddingX="4" fontWeight="semibold">
												<Money
													amount={changeDetails.currentPrice}
													currency={changeDetails.currency}
													showCurrency
												/>
											</Box>

											{changeDetails.currentConfigurations.map(
												(config, index) => (
													<Box paddingX="2" paddingBottom="2" key={index}>
														<Inline
															bgColor="gray2"
															padding="2"
															borderRadius="md"
															gap="1"
														>
															<Money
																money={config.per_quantity_price}
																showCurrency
															/>
															<Text as="span">
																/{config.configuration_name} × {config.quantity}
																{config.complementary > 0 ? (
																	<Box title="Free of Cost">
																		({config.complementary} FOC)
																	</Box>
																) : (
																	""
																)}
															</Text>
														</Inline>
													</Box>
												)
											)}
										</Stack>
									</Inline>
									<Text>
										Please confirm that these changes are as per your
										expectation else, please modify the details before updating.
									</Text>
								</>
							)}
						</Stack>
					</ConfirmationDialog>
				</>
			)}
		</ConfirmationDialogProvider>
	)
}

type TTravelActivityBookingFormValue = {
	supplier?: ISupplier
	supplier_contact?: TSupplierContact
	confirmation_details?: string
	remarks?: string
	booked: boolean
	data: TTravelActivityDatewisePricesInputFieldValue
	members?: Array<ITouristOrGroupOption>
	all_tourists?: Array<TTourist>
	primary_contact?: number | null
	selectedDates?: Array<Date>
	tourists: TBookingTouristUpdate[] | undefined
}

type TValidTravelActivityBookingFormValue = Omit<
	TTravelActivityBookingFormValue,
	"data"
> & {
	data: TValidTravelActivityDatewisePricesInputFieldValue
}

function PreviewChanges({
	bookingData,
	tripData,
	dates,
	selectedDates,
}: {
	bookingData: TTravelActiviytBooking
	tripData: ITrip
	dates: Array<Date>
	selectedDates: Array<Date>
}) {
	// Get live form values using useFieldValue hooks
	const { value: currentSupplier } = useFieldValue<ISupplier | undefined>(
		"supplier"
	)
	const { value: _currentSupplierContact } = useFieldValue<
		TSupplierContact | undefined
	>("supplier_contact")
	const { value: currentConfirmationDetails } = useFieldValue<string>(
		"confirmation_details"
	)
	const { value: currentRemarks } = useFieldValue<string>("remarks")
	const { value: currentActivity } = useFieldValue<TTravelActivity | undefined>(
		"data.activity"
	)
	const { value: currentTicketType } = useFieldValue<
		TTravelActivityTicketType | undefined
	>("data.ticket_type")
	const { value: currentSlot } = useFieldValue<string>("data.slot")
	const { value: currentDuration } = useFieldValue<
		{ iso: string; name: string } | undefined
	>("data.duration")
	const { value: _currentConfigurations } = useFieldValue<
		Array<TTicketTouristConfiguration> | undefined
	>("data.ticket_tourist_configurations")
	const { value: currentPickUpLocation } = useFieldValue<
		string | TTransportServiceLocationPoint | undefined
	>("data.pick_up_location")
	const { value: currentDropLocation } = useFieldValue<
		string | TTransportServiceLocationPoint | undefined
	>("data.drop_location")
	const { value: currentUseHotelAsPickup } = useFieldValue<null | string>(
		"data.use_hotel_as_pickup"
	)
	const { value: currentUseHotelAsDrop } = useFieldValue<null | string>(
		"data.use_hotel_as_drop"
	)

	const currentConfigurations = useMemo(
		() => _currentConfigurations || [],
		[_currentConfigurations]
	)

	const currentSupplierContact = useMemo(
		() => (currentSupplier ? _currentSupplierContact || undefined : undefined),
		[_currentSupplierContact, currentSupplier]
	)

	const generatePreviewRows = (): React.ReactNode[][] => {
		const rows: React.ReactNode[][] = []

		// Compare dates
		const initialDate = dates[0]
		const currentDate = selectedDates[0]
		if (
			initialDate &&
			currentDate &&
			!isSame(initialDate, currentDate, "day")
		) {
			const initialDateFormatted = formatDate(initialDate, "dddd D MMM, YYYY")
			const currentDateFormatted = formatDate(currentDate, "dddd D MMM, YYYY")

			// Calculate day number
			const tripStartDate = new Date(
				localOrUtcTimestampToLocalDate(
					tripData.start_date_local,
					tripData.start_date
				)
			)
			const initialDayNumber = getDiff(initialDate, tripStartDate, "day") + 1
			const currentDayNumber = getDiff(currentDate, tripStartDate, "day") + 1

			rows.push([
				<Stack gap="1">
					<Text fontWeight="semibold">Date: {initialDateFormatted}</Text>
					<Text fontSize="sm">({withOrdinalSuffix(initialDayNumber)} Day)</Text>
				</Stack>,
				<Stack gap="1">
					<Text fontWeight="semibold">Date: {currentDateFormatted}</Text>
					<Text fontSize="sm">({withOrdinalSuffix(currentDayNumber)} Day)</Text>
				</Stack>,
			])
		}

		// Compare activity details
		const initialActivity = bookingData.activity
		const initialTicketType = bookingData.ticket_type
		const initialSlot = bookingData.slot
		const initialDuration = bookingData.duration

		const activityChanged =
			initialActivity?.id !== currentActivity?.id ||
			initialTicketType?.id !== currentTicketType?.id ||
			(initialSlot ?? "") !== (currentSlot ?? "") ||
			(initialDuration ?? "") !== (currentDuration?.iso ?? "") ||
			extractLocationString(bookingData.pick_up_location) !==
				extractLocationString(currentPickUpLocation) ||
			extractLocationString(bookingData.drop_location) !==
				extractLocationString(currentDropLocation) ||
			bookingData.use_hotel_as_pickup !== currentUseHotelAsPickup ||
			bookingData.use_hotel_as_drop !== currentUseHotelAsDrop

		const { end_time } = getStartEndDateTimesFromDateAndSlot(
			currentDate,
			currentSlot,
			currentDuration?.iso
		)

		if (activityChanged) {
			const initialActivityText = `${initialActivity?.name} - ${initialTicketType?.name || "N/A"}`
			const currentActivityText = `${currentActivity?.name} - ${currentTicketType?.name || "N/A"}`

			const initialSlotText =
				initialSlot || bookingData.duration_formatted
					? `${initialSlot ?? ""} (${bookingData.duration_formatted || "N/A"}${bookingData.end_time_formatted ? ` - ends at ${bookingData.end_time_formatted}` : ""})`
					: "N/A"
			const currentSlotText =
				currentSlot || currentDuration?.name
					? `${currentSlot} (${currentDuration?.name || "N/A"}${end_time ? ` - ends at ${end_time}` : ""})`
					: "N/A"

			const getPickupDropText = (
				loc: string | TTransportServiceLocationPoint | undefined,
				preference?: string | null
			) => {
				if (preference === "check_in") return "Check-in Hotel"
				if (preference === "check_out") return "Check-out Hotel"
				const location = extractLocationString(loc)
				return location
			}

			const initialPickupText = getPickupDropText(
				bookingData.pick_up_location,
				bookingData.use_hotel_as_pickup
			)
			const initialDropText = getPickupDropText(
				bookingData.drop_location,
				bookingData.use_hotel_as_drop
			)

			const currentPickupText = getPickupDropText(
				currentPickUpLocation,
				currentUseHotelAsPickup
			)
			const currentDropText = getPickupDropText(
				currentDropLocation,
				currentUseHotelAsDrop
			)

			rows.push([
				<Stack gap="1">
					<Text fontWeight="semibold">{initialActivityText}</Text>
					<Text fontSize="sm" color="muted">
						{initialSlotText}
					</Text>
					<Stack gap="0">
						{(initialPickupText || currentPickupText) && (
							<Text fontSize="xs" color="muted">
								<strong>Pickup:</strong> {initialPickupText || "None"}
							</Text>
						)}
						{(initialDropText || currentDropText) && (
							<Text fontSize="xs" color="muted">
								<strong>Drop:</strong> {initialDropText || "None"}
							</Text>
						)}
					</Stack>
				</Stack>,
				<Stack gap="1">
					<Text fontWeight="semibold">{currentActivityText}</Text>
					<Text fontSize="sm" color="muted">
						{currentSlotText}
					</Text>
					<Stack gap="0">
						{(initialPickupText || currentPickupText) && (
							<Text fontSize="xs" color="muted">
								<strong>Pickup:</strong> {currentPickupText || "None"}
							</Text>
						)}
						{(initialDropText || currentDropText) && (
							<Text fontSize="xs" color="muted">
								<strong>Drop:</strong> {currentDropText || "None"}
							</Text>
						)}
					</Stack>
				</Stack>,
			])
		}

		// Compare configuration/rates
		const initialConfigurations = bookingData.ticket_tourist_configurations

		const configurationsChanged =
			!initialConfigurations ||
			!currentConfigurations ||
			initialConfigurations.length !== currentConfigurations.length ||
			initialConfigurations.some((initialConfig, index) => {
				const currentConfig = currentConfigurations[index]
				if (!currentConfig) return true

				const initialPrice =
					moneyParseByDecimal(
						initialConfig.booked_price,
						initialConfig.currency
					) || 0
				const currentPrice =
					moneyParseByDecimal(
						currentConfig.date_wise_prices?.[0]?.booked_price || 0,
						currentConfig.date_wise_prices?.[0]?.currency || "INR"
					) || 0

				return (
					initialConfig.configuration.id !== currentConfig.configuration?.id ||
					initialConfig.quantity !== currentConfig.quantity ||
					!isSameMoney(initialPrice, currentPrice)
				)
			})

		if (configurationsChanged) {
			const initialConfigText =
				initialConfigurations?.map((config) => {
					return (
						<Inline key={config.configuration.id} gap="1">
							<Text fontWeight="semibold" as="span">
								{config.configuration.name}
							</Text>
							{`: ${config.quantity}`}
							{config.complementary > 0 ? (
								<Box title="Free of Cost">({config.complementary}FOC)</Box>
							) : (
								""
							)}{" "}
							×
							<Money
								amount={config.per_quantity_booked_price || 0}
								currency={config.currency}
								showCurrency
							/>
							{` = `}
							<Money
								amount={config.booked_price || 0}
								currency={config.currency}
								showCurrency
							/>
						</Inline>
					)
				}) || "N/A"

			const currentConfigText =
				(currentConfigurations || []).map(
					(config: TTicketTouristConfiguration) => {
						const price = config.date_wise_prices?.[0]?.booked_price ?? 0
						const perQuantityPrice =
							config.date_wise_prices?.[0]?.per_quantity_booked_price ?? 0
						const currency = config.date_wise_prices?.[0]?.currency || "INR"

						return (
							<Inline key={config.configuration?.id || config.__id} gap="1">
								<Text fontWeight="semibold" as="span">
									{config.configuration?.name || "Unknown"}
								</Text>
								{`: ${config.quantity}`}
								{(config.complementary || 0) > 0 ? (
									<Box title="Free of Cost">({config.complementary}FOC)</Box>
								) : (
									""
								)}{" "}
								×
								<Money
									amount={perQuantityPrice}
									currency={currency}
									showCurrency
								/>
								{` = `}
								<Money amount={price} currency={currency} showCurrency />
							</Inline>
						)
					}
				) || "N/A"

			rows.push([
				<Stack gap="1">
					<Text fontSize="sm">{initialConfigText}</Text>
				</Stack>,
				<Stack gap="1">
					<Text fontSize="sm">{currentConfigText}</Text>
				</Stack>,
			])
		}

		// Compare supplier details
		const initialSupplier = bookingData.supplier
		const initialSupplierContact = bookingData.supplier_contact
		const initialConfirmationDetails = bookingData.confirmation_details
		const initialRemarks = bookingData.remarks

		const supplierChanged =
			initialSupplier?.id !== currentSupplier?.id ||
			initialSupplierContact?.id !== currentSupplierContact?.id ||
			(initialConfirmationDetails ?? "") !==
				(currentConfirmationDetails ?? "") ||
			(initialRemarks ?? "") !== (currentRemarks ?? "")

		if (supplierChanged) {
			const initialSupplierText = initialSupplier?.name || "N/A"
			const currentSupplierText = currentSupplier?.name || "N/A"

			const initialContactText = initialSupplierContact?.name || "N/A"
			const currentContactText = currentSupplierContact?.name || "N/A"

			rows.push([
				<Stack gap="1">
					<Inline gap="1">
						<Text fontWeight="semibold">Supplier:</Text>
						<Text color="muted">{initialSupplierText}</Text>
					</Inline>
					<Stack fontSize="sm" gap="1">
						<Inline gap="1">
							<Text fontWeight="semibold">Contact:</Text>
							<Text color="muted">{initialContactText}</Text>
						</Inline>
						{initialConfirmationDetails && (
							<Inline gap="1">
								<Text fontWeight="semibold">Confirmation:</Text>
								<Text color="muted">{initialConfirmationDetails}</Text>
							</Inline>
						)}
						{initialRemarks && (
							<Inline gap="1">
								<Text fontWeight="semibold">Remarks:</Text>
								<Text color="muted">{initialRemarks}</Text>
							</Inline>
						)}
					</Stack>
				</Stack>,
				<Stack gap="1">
					<Inline gap="1">
						<Text fontWeight="semibold">Supplier:</Text>
						<Text color="muted">{currentSupplierText}</Text>
					</Inline>
					<Stack fontSize="sm" gap="1">
						<Inline gap="1">
							<Text fontWeight="semibold">Contact:</Text>
							<Text color="muted">{currentContactText}</Text>
						</Inline>
						{currentConfirmationDetails && (
							<Inline gap="1">
								<Text fontWeight="semibold">Confirmation:</Text>
								<Text color="muted">{currentConfirmationDetails}</Text>
							</Inline>
						)}
						{currentRemarks && (
							<Inline gap="1">
								<Text fontWeight="semibold">Remarks:</Text>
								<Text color="muted">{currentRemarks}</Text>
							</Inline>
						)}
					</Stack>
				</Stack>,
			])
		}

		return rows
	}

	const rows = generatePreviewRows()

	return rows.length > 0 ? (
		<Stack gap="4">
			<Box
				bgColor="warning"
				paddingY="1"
				paddingX="2"
				rounded="md"
				marginRight="auto"
			>
				<Text fontWeight="semibold" fontSize="lg">
					Preview Changes
				</Text>
			</Box>
			<Table bordered hover responsive>
				<thead>
					<tr>
						<TableHeaderDataCell width={"1/2"} bgColor="subtle">
							Existing Details
						</TableHeaderDataCell>
						<TableHeaderDataCell
							width={"1/2"}
							bgColor="success"
							borderLeftWidth="1"
						>
							New Details
						</TableHeaderDataCell>
					</tr>
				</thead>
				<tbody>
					{rows.map((row, index) => (
						<tr key={index}>
							<TableDataCell>{row[0]}</TableDataCell>
							<TableDataCell borderLeftWidth="1">{row[1]}</TableDataCell>
						</tr>
					))}
				</tbody>
			</Table>
		</Stack>
	) : null
}

function EditForm({
	noOfAdults,
	children,
	dates,
	tripData,
	tripDestinations,
	bookingData,
	initialValues,
	tripId,
	onSubmit,
	onCancel,
}: {
	noOfAdults: number
	children: TChildrenArray
	dates: Array<Date>
	tripDestinations: Array<TTripDestination>
	tripData: ITrip
	bookingData: TTravelActiviytBooking
	initialValues: TValidTravelActivityBookingFormValue
	tripId: number
	onSubmit: (data: TValidTravelActivityBookingFormValue) => Promise<unknown>
	onCancel: () => void
}) {
	const [selectedDates, setSelectedDates] = useState<Array<Date>>(dates)

	// Generate booking date options from trip data
	const bookingDateOptions = useMemo(() => {
		if (!tripData.start_date || !tripData.end_date) return []

		const startDate = new Date(
			localOrUtcTimestampToLocalDate(
				tripData.start_date_local,
				tripData.start_date
			)
		)
		const endDate = new Date(
			localOrUtcTimestampToLocalDate(tripData.end_date_local, tripData.end_date)
		)

		const utilityOptions =
			createBookingDateSelectionOptionsFromStartAndEndDates(startDate, endDate)

		const baseOptions = utilityOptions.map((option) => ({
			id: option.id,
			name: option.name,
			value: option.id,
		}))

		// Check if current selected date is outside the trip range
		const currentDate = selectedDates[0]
		if (currentDate && !isNaN(currentDate.getTime())) {
			const currentDateFormatted = formatDate(currentDate, "YYYY-MM-DD")

			// Check if current date is already in the base options
			const isInBaseOptions = baseOptions.find(
				(option) => option.value === currentDateFormatted
			)

			// If not in base options, add it to the beginning
			if (!isInBaseOptions) {
				const dayOfWeek = formatDate(currentDate, "ddd")
				const month = formatDate(currentDate, "MMM")
				const date = formatDate(currentDate, "D")

				baseOptions.unshift({
					id: currentDateFormatted,
					name: `Current: ${dayOfWeek} ${date} ${month}`,
					value: currentDateFormatted,
				})
			}
		}

		return baseOptions
	}, [tripData, selectedDates])

	// Handle date change
	const handleDateChange = (
		selectedOption: (typeof bookingDateOptions)[0] | undefined
	) => {
		if (selectedOption) {
			const selectedDate = parseDate(selectedOption.value, "YYYY-MM-DD")
			setSelectedDates([selectedDate])
		} else {
			setSelectedDates([])
		}
	}
	return (
		<Form<TValidTravelActivityBookingFormValue>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				const data = getValidTravelActivityData(values.data)
				if (!data) {
					throw new Error("Please provide valid data")
				}
				await onSubmit({
					...values,
					data,
					selectedDates,
				})
			})}
			validate={validateFormValues(
				Validator.object().shape({
					confirmation_details: Validator.string()
						.nullable()
						.max(1000, "Maximum 1000 characters allowed"),
					data: Validator.object().shape({
						ticket_tourist_configurations: Validator.array().of(
							Validator.object().shape({
								date_wise_prices: Validator.array().of(
									Validator.object().shape({
										per_quantity_booked_price: EmptyNumberValidator()
											.required("Please provide a booked price")
											.min(0, "Booked price should be non negative"),
									})
								),
								quantity: EmptyNumberValidator()
									.required("Please provide a quantity")
									.min(1, "Quantity should be a positive value"),
								complementary: EmptyNumberValidator()
									.min(0, "FOC should be 0 or a positive integer")
									.integer("FOC should be a positive integer")
									.test(
										"less-than-quantity",
										"FOC must be less than Quantity",
										function (value) {
											const { quantity } = this.parent
											if (value == null || quantity == null) return true
											return value < quantity
										}
									),
							})
						),
					}),
				})
			)}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ handleSubmit, submitting, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="3">
						<Grid gap="4">
							<Col sm={12} md={2}>
								<Stack gap="1">
									<Heading as="h3">Trip Details</Heading>
								</Stack>
							</Col>
							<Col>
								<Inline gap="6" flexWrap="wrap">
									<Stack gap="px">
										<Text fontSize="sm" color="muted" fontWeight="semibold">
											Destinations
										</Text>
										<Text>
											{tripDestinations.map((t) => t.name).join(", ")}
										</Text>
									</Stack>
									<Stack gap="px">
										<Text fontSize="sm" color="muted" fontWeight="semibold">
											Trip Start Date
										</Text>
										<Text>
											<Time
												timestamp={tripData.start_date}
												localTimestamp={tripData.start_date_local}
											/>
										</Text>
									</Stack>
									<Stack gap="px">
										<Text fontSize="sm" color="muted" fontWeight="semibold">
											Trip End Date
										</Text>
										<Text>
											<Time
												timestamp={tripData.end_date}
												localTimestamp={tripData.end_date_local}
											/>
										</Text>
									</Stack>
									<Stack gap="px">
										<Text fontSize="sm" color="muted" fontWeight="semibold">
											Pax
										</Text>
										<Text>{tripData.pax_string}</Text>
									</Stack>
								</Inline>
							</Col>
						</Grid>
						<Divider sm marginY="2" />
						<Stack gap="3">
							<Inline gap="4" alignItems="center">
								<Component initialState={false}>
									{({ state: isEditingDate, setState: setIsEditingDate }) =>
										isEditingDate ? (
											<Inline gap="3" flex="1">
												<Stack maxWidth="sm" flex="1" gap="1">
													<Text fontWeight="semibold">Day</Text>
													<Select
														options={bookingDateOptions}
														searchable={false}
														placeholder="Select day..."
														fullWidth
														multiple={false}
														value={bookingDateOptions.find(
															(option) =>
																option.value ===
																formatDate(selectedDates[0], "YYYY-MM-DD")
														)}
														onChange={handleDateChange}
													/>
													{initialValues.supplier &&
														initialValues.supplier.name && (
															<Text color="warning" fontSize="sm">
																This activity is already assigned to the{" "}
																{initialValues.supplier.name} supplier. Please
																inform the supplier about the date change.
															</Text>
														)}
												</Stack>
												<Inline gap="3" paddingTop="6">
													<Button
														onClick={() => {
															setIsEditingDate(false)
															setSelectedDates([
																parseDate(
																	formatDate(dates[0], "YYYY-MM-DD"),
																	"YYYY-MM-DD"
																),
															])
														}}
													>
														<Icons.Cancel title="Cancel" />
													</Button>
													<Button onClick={() => setIsEditingDate(false)}>
														<Icons.Ok title="Save" />
													</Button>
												</Inline>
											</Inline>
										) : (
											<>
												<Text>
													{formatDate(selectedDates[0], "dddd, D MMM")}
												</Text>
												<Button
													size="sm"
													onClick={() => setIsEditingDate(true)}
												>
													<Icons.Pencil title="Edit" />
												</Button>
											</>
										)
									}
								</Component>
							</Inline>
							<TravelActivityDatewisePricesInputField
								name="data"
								noOfAdults={noOfAdults}
								children={children}
								dates={selectedDates}
								tripDestinations={tripDestinations}
								showBookedPrices
								canCustomizeDuration
								allowAddingPickupDrop
								activityCreatables={{
									activity: true,
									activityType: false,
								}}
							/>
						</Stack>
						<Grid>
							<Col xs={12} sm={6} lg={2}>
								{/* <Inline gap="4" flexWrap={"wrap"}> */}
								<SelectField
									label="Service Provider"
									fullWidth
									select={SelectTravelActivitySuppliers}
									name={`supplier`}
									onChange={(value: ISupplier | undefined) => {
										form.batch(() => {
											form.change("supplier_contact", undefined)
											form.change("supplier", value)
										})
									}}
									creatable
									multiple={false}
									placeholder="Select or Add new..."
								/>
							</Col>
							<Col xs={12} sm={6} lg={2}>
								<GetFieldValue<ISupplier | undefined> name="supplier">
									{({ value }) =>
										!value ? null : (
											<Box flex="1">
												<SelectField
													label="Point of Contact"
													fullWidth
													select={SelectSupplierContact}
													supplierId={value.id}
													name={`supplier_contact`}
													creatable
													multiple={false}
													placeholder="Select or Add new..."
												/>
											</Box>
										)
									}
								</GetFieldValue>
							</Col>
							<Col xs={12} sm={6} lg={4}>
								<Box flex="1" style={{ minWidth: "200px" }}>
									<TextAreaInputField
										name="confirmation_details"
										label="Confirmation Details"
										placeholder="Enter the booking confirmation details here..."
										rows={3}
									/>
								</Box>
							</Col>
							<Col xs={12} sm={6} lg={4}>
								<Box flex="1" style={{ minWidth: "200px" }}>
									<TextAreaInputField
										name="remarks"
										label="Remarks for Supplier and Customer"
										secondaryLabel="optional"
										placeholder="Notes and remarks to be shared with supplier and customer..."
										rows={3}
									/>
								</Box>
							</Col>
						</Grid>
						<Divider sm marginY="2" />
						{/* Assign Tourists Section */}
						<Card maxWidth={"3xl"} marginBottom={"0"} bgColor="subtle">
							<CardBody>
								<SwitchInputField
									name="booked"
									label="Confirmed by Supplier"
									help="This will generate instalments for this booking for the supplier"
								/>
								<GetFieldValue<
									Required<TValidTravelActivityBookingFormValue>["booked"]
								>
									name={`booked`}
								>
									{({ value: booked }) => (
										<GetFieldValue<
											Required<TValidTravelActivityBookingFormValue>["supplier"]
										>
											name={`supplier`}
										>
											{({ value: supplier }) =>
												supplier && booked ? (
													<Stack marginLeft="4">
														<Divider sm />
														<ActivityPaymentPreferencesForm
															supplierId={supplier.id}
														/>
													</Stack>
												) : null
											}
										</GetFieldValue>
									)}
								</GetFieldValue>
							</CardBody>
						</Card>
						<Divider sm marginY="2" />
						{/* Assign Tourists Section */}
						<Card maxWidth={"3xl"} marginBottom={"0"}>
							<CardBody>
								<GetFieldValue<
									TValidTravelActivityDatewisePricesInputFieldValue["ticket_tourist_configurations"]
								> name="data.ticket_tourist_configurations">
									{({ value: ticket_tourist_configurations }) => {
										const totalBookingAdults = (
											ticket_tourist_configurations || []
										).length
											? ticket_tourist_configurations
													.filter(
														(t) => t.configuration.tourist_type === "adult"
													)
													.reduce((acc, q) => acc + (q.quantity || 0), 0)
											: undefined
										const totalBookingChildren = (
											ticket_tourist_configurations || []
										).length
											? ticket_tourist_configurations
													.filter(
														(t) => t.configuration.tourist_type === "child"
													)
													.reduce((acc, q) => acc + (q.quantity || 0), 0)
											: undefined

										return (
											<GetFieldValue<
												TTourist[] | undefined
											> name="all_tourists">
												{({ value: allTourists }) => (
													<GetFieldValue<number | null> name="primary_contact">
														{({
															value: primaryContact,
															onChange: setPrimaryContact,
														}) => (
															<GetFieldValue<
																TBookingTouristUpdate[] | undefined
															> name="tourists">
																{({ value: bookingTourists }) => (
																	<TouristAssignmentInputField
																		name={"members"}
																		tripId={tripId}
																		requiredAdults={totalBookingAdults}
																		requiredChildren={totalBookingChildren}
																		allTourists={allTourists}
																		onChange={(value) => {
																			// Map ITouristOrGroupOption[] -> expected TTravelActivityMember[] shape
																			form.change("members", value)

																			const tourists = (value || [])
																				.filter(
																					(m) => m.member_type === "tourists"
																				)
																				.map((m) => m.member as TTourist)
																			const groups = (value || [])
																				.filter(
																					(m) =>
																						m.member_type === "tourist_groups"
																				)
																				.map((m) => m.member as TTouristGroup)

																			const allUniqueTourists = [
																				...tourists,
																				...(groups || []).flatMap((g) =>
																					(g || []).members.map(
																						(m) => m.tourist
																					)
																				),
																			].filter(
																				(t, index, self) =>
																					self.findIndex(
																						(t2) => t2.id === t.id
																					) === index
																			)

																			form.change(
																				"all_tourists",
																				allUniqueTourists
																			)
																		}}
																		label="Assign Tourists to this Booking"
																		primaryContact={primaryContact}
																		onPrimaryContactChange={(touristId) => {
																			setPrimaryContact(touristId || null)
																		}}
																		bookingTourist={bookingTourists}
																		onBookingTouristChange={(
																			updatedTourists
																		) => {
																			form.change("tourists", updatedTourists)
																		}}
																	/>
																)}
															</GetFieldValue>
														)}
													</GetFieldValue>
												)}
											</GetFieldValue>
										)
									}}
								</GetFieldValue>
							</CardBody>
						</Card>
						<Divider sm />
						<PreviewChanges
							bookingData={bookingData}
							tripData={tripData}
							dates={dates}
							selectedDates={selectedDates}
						/>
						<Divider sm />
						<SubmissionError />
						<Inline gap="4" justifyContent="end">
							<Button disabled={submitting} type="submit">
								{submitting ? "Please wait..." : "Save Details"}
							</Button>
							<Button disabled={submitting} onClick={() => onCancel()}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}
